import { type FormEvent, useId, useState } from 'react';

import type { NoticeAudienceOptions } from '@/shared/notices/types';
import { useCreateNotice, useNoticeAudience } from '@/shared/notices/useNotices';
import { PROFILE_LABELS, PROFILE_ORDER, type UserProfile } from '@/shared/session/profiles';
import { Button } from '@/shared/ui/Button';
import { Dialog } from '@/shared/ui/Dialog';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { SearchIcon } from '@/shared/ui/icons';
import { Input } from '@/shared/ui/Input';

import { audienceCount, dayInput, matchesPerson, peopleCount } from './noticeText';

const TITLE_MAX = 160;
const BODY_MAX = 2000;

/** The thirty days a notice stays in the window when nobody says until when. */
const DEFAULT_DAYS = 30;

/** Past this many people the list gets a search box: Cubas has some twenty. */
const SEARCH_FROM = 8;

type Scope = 'everyone' | 'chosen';

/**
 * A notice by hand (contract v10.50): what Víctor asked for on 24/09, «un mensaje para que
 * todo el mundo lo vea». Since v10.55 (ADR 0052 of the back) for the whole team, as before
 * and by default, or for the profiles and people chosen here, anybody in either. Only
 * whoever administers reaches it; the API says no to anybody else anyway. «Hasta el» is
 * the last day it comes up on coming in («a partir del 7 de mayo que no salga más»); after
 * that it stays under the bell. With a back before v10.55 there is nothing to choose from
 * and it writes for everybody, as it did.
 */
export function WriteNotice({ onDone }: { onDone: () => void }) {
  const id = useId();
  const create = useCreateNotice();
  const audience = useNoticeAudience();
  const options = audience.data?.profiles ? audience.data : undefined;
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [until, setUntil] = useState(() => dayInput(DEFAULT_DAYS));
  const [scope, setScope] = useState<Scope>('everyone');
  const [profiles, setProfiles] = useState<ReadonlySet<UserProfile>>(new Set());
  const [people, setPeople] = useState<ReadonlySet<string>>(new Set());
  const [tried, setTried] = useState(false);
  const titleError = title.trim() ? undefined : 'Escribe de qué va el aviso.';
  const chosen = scope === 'chosen' && options !== undefined;
  const audienceError =
    chosen && profiles.size === 0 && people.size === 0
      ? 'Elige al menos un perfil o una persona.'
      : undefined;

  function submit(event: FormEvent) {
    event.preventDefault();
    setTried(true);
    if (titleError || audienceError) return;
    create.mutate(
      {
        title: title.trim(),
        body: body.trim(),
        until: until || null,
        ...(chosen
          ? {
              profiles: PROFILE_ORDER.filter((each) => profiles.has(each)),
              people: [...people],
            }
          : {}),
      },
      { onSuccess: onDone },
    );
  }

  return (
    <Dialog
      title="Escribir un aviso"
      description="Sale al entrar en la plataforma hasta que se diga «Entendido», a todo el equipo o a quien elijas."
      onClose={onDone}
      busy={create.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onDone} disabled={create.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form={`${id}-form`} disabled={create.isPending}>
            {create.isPending ? 'Publicando…' : 'Publicar el aviso'}
          </Button>
        </>
      }
    >
      <form id={`${id}-form`} onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Input
          label="Título"
          value={title}
          maxLength={TITLE_MAX}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="Inventario el lunes 20"
          error={tried ? titleError : undefined}
        />
        <div className="flex flex-col gap-1">
          <label htmlFor={`${id}-body`} className="text-ink-2 text-[13.5px] font-medium">
            Texto <span className="text-ink-3 font-normal">opcional</span>
          </label>
          <textarea
            id={`${id}-body`}
            className="field h-auto min-h-28 py-2 text-[14px]"
            rows={5}
            maxLength={BODY_MAX}
            value={body}
            aria-describedby={`${id}-body-hint`}
            onChange={(event) => setBody(event.target.value)}
            placeholder="La tienda cierra a las 14:00 para contar."
          />
          <p id={`${id}-body-hint`} className="text-ink-3 text-[12.5px]">
            {body.length} de {BODY_MAX}.
          </p>
        </div>
        <Input
          type="date"
          label="Sale al entrar hasta el"
          value={until}
          min={dayInput(0)}
          onChange={(event) => setUntil(event.target.value)}
          hint="Ese día incluido. Después sigue en la campana."
          className="w-44"
        />
        <fieldset className="flex flex-col gap-2">
          <legend className="text-ink-2 mb-1 text-[13.5px] font-medium">A quién</legend>
          <label className="text-ink-2 flex items-start gap-2 text-[14px]">
            <input
              type="radio"
              name={`${id}-scope`}
              className="accent-accent mt-0.5 size-4"
              checked={scope === 'everyone'}
              onChange={() => setScope('everyone')}
            />
            <span>Todo el equipo</span>
          </label>
          {audience.isError ? null : (
            <label className="text-ink-2 flex items-start gap-2 text-[14px]">
              <input
                type="radio"
                name={`${id}-scope`}
                className="accent-accent mt-0.5 size-4"
                checked={scope === 'chosen'}
                disabled={options === undefined}
                onChange={() => setScope('chosen')}
              />
              <span>
                Elegir a quién
                <span className="text-ink-3 block text-[12.5px] leading-snug">
                  Unos perfiles, unas personas o las dos cosas. Le sale a quien esté en cualquiera.
                </span>
              </span>
            </label>
          )}
          {chosen ? (
            <AudiencePicker
              options={options}
              profiles={profiles}
              people={people}
              onProfiles={setProfiles}
              onPeople={setPeople}
              error={tried ? audienceError : undefined}
            />
          ) : null}
        </fieldset>
        {create.isError ? (
          <ErrorMessage error={create.error}>No se pudo publicar el aviso</ErrorMessage>
        ) : null}
      </form>
    </Dialog>
  );
}

/**
 * The profiles and the people of «Elegir a quién». Whoever a chosen profile already
 * reaches shows ticked and greyed, with the profile that reaches them: unticking them would
 * change nothing. A superadministrator has no profile here, only their name.
 */
function AudiencePicker({
  options,
  profiles,
  people,
  onProfiles,
  onPeople,
  error,
}: {
  options: NoticeAudienceOptions;
  profiles: ReadonlySet<UserProfile>;
  people: ReadonlySet<string>;
  onProfiles: (next: ReadonlySet<UserProfile>) => void;
  onPeople: (next: ReadonlySet<string>) => void;
  error: string | undefined;
}) {
  const id = useId();
  const [search, setSearch] = useState('');
  const everybody = options.people ?? [];
  const shown = everybody.filter((person) => matchesPerson(person, search));
  const count = audienceCount(everybody, profiles, people);
  const nobodyChosen = profiles.size === 0 && people.size === 0;

  function toggle<T>(set: ReadonlySet<T>, value: T, on: boolean): Set<T> {
    const next = new Set(set);
    if (on) next.add(value);
    else next.delete(value);
    return next;
  }

  return (
    <div className="border-line-2 flex flex-col gap-3 rounded-md border p-3 sm:ml-6">
      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-ink-2 mb-1 text-[13px] font-medium">Perfiles</legend>
        {options.profiles.map((each) => {
          const members = everybody.filter((person) => person.profile === each.profile).length;
          return (
            <label key={each.profile} className="text-ink-2 flex items-center gap-2 text-[14px]">
              <input
                type="checkbox"
                className="accent-accent size-4"
                checked={profiles.has(each.profile)}
                onChange={(event) =>
                  onProfiles(toggle(profiles, each.profile, event.target.checked))
                }
              />
              <span>
                {each.label}
                <span className="text-ink-3 text-[12.5px]"> · {peopleCount(members)}</span>
              </span>
            </label>
          );
        })}
      </fieldset>
      <fieldset className="flex min-w-0 flex-col gap-1.5">
        <legend className="text-ink-2 mb-1 text-[13px] font-medium">Personas</legend>
        {everybody.length > SEARCH_FROM ? (
          <div className="relative">
            <SearchIcon
              size={16}
              className="text-ink-4 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
            />
            <input
              type="search"
              aria-label="Buscar personas"
              placeholder="Buscar por nombre"
              className="field w-full pl-9"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        ) : null}
        {everybody.length === 0 ? (
          <p className="text-ink-3 text-[13px]">No hay nadie más en la plataforma.</p>
        ) : (
          <ul className="flex max-h-52 flex-col gap-1.5 overflow-y-auto">
            {shown.map((person) => {
              const via =
                person.profile != null && profiles.has(person.profile) ? person.profile : null;
              const hint = via
                ? `Ya le llega por ${PROFILE_LABELS[via]}`
                : person.profile
                  ? PROFILE_LABELS[person.profile]
                  : 'Superadministrador';
              return (
                <li key={person.id}>
                  <label className="text-ink-2 flex items-start gap-2 text-[14px]">
                    <input
                      type="checkbox"
                      className="accent-accent mt-0.5 size-4 shrink-0"
                      checked={via !== null || people.has(person.id)}
                      disabled={via !== null}
                      onChange={(event) =>
                        onPeople(toggle(people, person.id, event.target.checked))
                      }
                    />
                    <span className="min-w-0 break-words">
                      {person.name}
                      <span className="text-ink-3 block text-[12.5px] leading-snug">{hint}</span>
                    </span>
                  </label>
                </li>
              );
            })}
            {shown.length === 0 ? (
              <li className="text-ink-3 text-[13px]">Nadie con ese nombre.</li>
            ) : null}
          </ul>
        )}
      </fieldset>
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-danger text-[13px]">
          {error}
        </p>
      ) : (
        <p className="text-ink-2 text-[13px]" aria-live="polite">
          {nobodyChosen
            ? 'Marca a quién le sale.'
            : count === 0
              ? 'Ahora mismo no le saldría a nadie.'
              : `Le saldrá a ${peopleCount(count)}.`}
        </p>
      )}
    </div>
  );
}
